import { useState, useRef, useEffect, type ReactNode } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '@/lib/supabase'
import { FORM_ANALYTICS, track, STEP_NUMBER, type FormStepName, type CtaLocation } from '@/lib/analytics'
import { Logo } from './Logo'
import { Calendar } from 'lucide-react'

/* ── Types ──────────────────────────────────────────────── */
interface Message {
  id: number
  from: 'bot' | 'user'
  content: ReactNode
}

type StepKey =
  | 'welcome'
  | 'nome'
  | 'telefone'
  | 'email'
  | 'clinica'
  | 'especialidade'
  | 'pacientes'
  | 'faixas'
  | 'desafio'
  | 'done'

interface Answers {
  nome: string
  telefone: string
  email: string
  clinica: string
  especialidade: string
  pacientes: string
  faixas: string[]
  desafio: string
}

/* ── Integrações (PRÓPRIAS do SecBrain — ver .env) ──────── */
// Webhook de CRM próprio. Vazio = não envia (evita misturar com o educascale).
const WEBHOOK_URL = import.meta.env.VITE_LEAD_WEBHOOK_URL || ''
// WhatsApp de contato (só dígitos). Vazio = botão oculto na tela final.
const WHATSAPP_NUMBER = (import.meta.env.VITE_WHATSAPP_NUMBER || '').replace(/\D/g, '')
// Link de agendamento do Cal.com (self-hosted). Prefill com nome/e-mail do lead.
const CAL_BOOKING_URL = import.meta.env.VITE_CAL_BOOKING_URL || 'https://agenda.neurodivergente.secbra.in/yan/30min'

const ESPECIALIDADES = [
  'Neuropediatria',
  'Psiquiatria Infantil',
  'Psicologia / ABA',
  'Terapia Ocupacional',
  'Fonoaudiologia',
  'Clínica Multidisciplinar',
  'Outra especialidade',
]

const PACIENTES_OPTIONS = [
  'Até 10',
  '11 a 30',
  '31 a 50',
  '51 a 100',
  'Mais de 100',
]

const FAIXAS = [
  'Crianças (0–12)',
  'Adolescentes (13–17)',
  'Adultos (18+)',
]

const DESAFIOS = [
  'Organizar os relatos das famílias',
  'Acompanhar a evolução entre consultas',
  'Identificar padrões e sinais de alerta',
  'Falta de tempo para registros',
  'Comunicação com as famílias',
]

/* ── Helpers ─────────────────────────────────────────────── */
const delay = (ms: number) => new Promise(r => setTimeout(r, ms))

function formatPhone(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 11)
  if (digits.length <= 2) return `(${digits}`
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
}

function whatsappUrl() {
  const msg = encodeURIComponent(
    'Olá! Acabei de preencher o agendamento no site do SecBrain e gostaria de falar com a equipe.'
  )
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`
}

function calBookingUrl(nome: string, email: string) {
  const params = new URLSearchParams()
  if (nome) params.set('name', nome)
  if (email) params.set('email', email)
  const qs = params.toString()
  return qs ? `${CAL_BOOKING_URL}?${qs}` : CAL_BOOKING_URL
}

/* ── Persistência: webhook + Supabase (próprios) ────────── */
async function sendWebhook(payload: Record<string, unknown>) {
  if (!WEBHOOK_URL) return
  try {
    await fetch(WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
  } catch (err) {
    console.warn('[SecBrain] Webhook failed', err)
  }
}

async function sendSupabase(payload: Record<string, unknown>) {
  if (!supabase) return
  try {
    await supabase.from('leads').insert(payload)
  } catch (err) {
    console.warn('[SecBrain] Supabase failed', err)
  }
}

function isLocalPreview() {
  if (typeof window === 'undefined') return false
  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname)
}

/** Envia para os destinos configurados em paralelo */
async function persistLead(payload: Record<string, unknown>) {
  if (isLocalPreview()) {
    console.info('[SecBrain] Lead persistence skipped on local preview', payload.type)
    return
  }
  await Promise.allSettled([sendWebhook(payload), sendSupabase(payload)])
}

/* ── Component ───────────────────────────────────────────── */
export default function SchedulingChat({
  open,
  onClose,
  source,
}: {
  open: boolean
  onClose: () => void
  source?: CtaLocation | 'custom_event' | 'unknown'
}) {
  const [messages, setMessages] = useState<Message[]>([])
  const [step, setStep] = useState<StepKey>('welcome')
  const [input, setInput] = useState('')
  const [typing, setTyping] = useState(false)
  const [selectedFaixas, setSelectedFaixas] = useState<string[]>([])
  const [answers, setAnswers] = useState<Answers>({
    nome: '',
    telefone: '',
    email: '',
    clinica: '',
    especialidade: '',
    pacientes: '',
    faixas: [],
    desafio: '',
  })
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const idRef = useRef(0)
  const partialSentRef = useRef(false)
  const startedAtRef = useRef<number | null>(null)
  const lastCompletedStepRef = useRef<FormStepName | null>(null)
  const stepRef = useRef<StepKey>('welcome')

  const nextId = () => ++idRef.current

  function trackStepComplete(stepName: FormStepName) {
    lastCompletedStepRef.current = stepName
  }

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, typing])

  // Focus input when step changes
  useEffect(() => {
    if (step === 'nome' || step === 'telefone' || step === 'email' || step === 'clinica') {
      setTimeout(() => inputRef.current?.focus(), 300)
    }
  }, [step])

  // Start conversation
  useEffect(() => {
    if (open && messages.length === 0) {
      startConversation()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Keep step ref in sync for close-handler
  useEffect(() => {
    stepRef.current = step
  }, [step])

  // Track open/close lifecycle
  useEffect(() => {
    if (open) {
      startedAtRef.current = Date.now()
      lastCompletedStepRef.current = null
      return
    }
    if (startedAtRef.current == null) return

    const timeSpent = Math.round((Date.now() - startedAtRef.current) / 1000)
    if (stepRef.current !== 'done') {
      track('form_abandon', {
        ...FORM_ANALYTICS,
        last_step: lastCompletedStepRef.current ?? null,
        last_step_number: lastCompletedStepRef.current
          ? STEP_NUMBER[lastCompletedStepRef.current]
          : 0,
        time_spent_seconds: timeSpent,
        source: source ?? 'unknown',
      })
    }
    startedAtRef.current = null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  async function addBotMessage(content: ReactNode) {
    setTyping(true)
    await delay(600 + Math.random() * 400)
    setTyping(false)
    setMessages(prev => [...prev, { id: nextId(), from: 'bot', content }])
  }

  function addUserMessage(content: string) {
    setMessages(prev => [...prev, { id: nextId(), from: 'user', content }])
  }

  async function startConversation() {
    await addBotMessage('Olá! Sou a assistente do SecBrain 👋')
    await addBotMessage('Vou te ajudar a agendar uma demonstração gratuita. São só algumas perguntas rápidas.')
    await addBotMessage('Qual é o seu nome?')
    setStep('nome')
  }

  /* ── Envio parcial: nome + telefone + email ──────────── */
  async function sendPartialLead(data: Pick<Answers, 'nome' | 'telefone' | 'email'>) {
    if (partialSentRef.current) return
    partialSentRef.current = true

    track('generate_lead', {
      ...FORM_ANALYTICS,
      lead_type: 'partial',
      fields_collected: ['nome', 'telefone', 'email'],
      source: source ?? 'unknown',
    })

    persistLead({
      type: 'partial',
      nome: data.nome,
      telefone: data.telefone,
      email: data.email,
      source: 'landing_chat',
    })
  }

  /* ── Envio completo ──────────────────────────────────── */
  async function sendCompleteLead(data: Answers) {
    track('generate_lead', {
      ...FORM_ANALYTICS,
      lead_type: 'complete',
      fields_collected: [
        'nome',
        'telefone',
        'email',
        'clinica',
        'especialidade',
        'num_pacientes',
        'faixas_etarias',
        'desafio',
      ],
      source: source ?? 'unknown',
    })

    persistLead({
      type: 'complete',
      nome: data.nome,
      telefone: data.telefone,
      email: data.email,
      clinica: data.clinica,
      especialidade: data.especialidade,
      num_pacientes: data.pacientes,
      faixas_etarias: data.faixas,
      desafio: data.desafio,
      source: 'landing_chat',
    })
  }

  async function handleTextSubmit() {
    const value = input.trim()
    if (!value) return
    setInput('')

    switch (step) {
      case 'nome':
        addUserMessage(value)
        setAnswers(a => ({ ...a, nome: value }))
        trackStepComplete('nome')
        await addBotMessage(`Prazer, ${value}! Qual o seu telefone com DDD?`)
        setStep('telefone')
        break

      case 'telefone': {
        const digits = value.replace(/\D/g, '')
        if (digits.length < 10) {
          track('form_validation_error', {
            ...FORM_ANALYTICS,
            step_name: 'telefone',
            reason: 'invalid_format',
            source: source ?? 'unknown',
          })
          await addBotMessage('Preciso de um telefone válido com DDD. Ex: (11) 99999-0000')
          return
        }
        addUserMessage(formatPhone(value))
        setAnswers(a => ({ ...a, telefone: digits }))
        trackStepComplete('telefone')
        await addBotMessage('E o seu melhor e-mail?')
        setStep('email')
        break
      }

      case 'email': {
        if (!value.includes('@') || !value.includes('.')) {
          track('form_validation_error', {
            ...FORM_ANALYTICS,
            step_name: 'email',
            reason: 'invalid_format',
            source: source ?? 'unknown',
          })
          await addBotMessage('Parece que esse e-mail não está correto. Pode verificar?')
          return
        }
        addUserMessage(value)
        const updated = { ...answers, email: value }
        setAnswers(updated)
        trackStepComplete('email')

        // 1o envio: lead parcial (nome + telefone + email)
        sendPartialLead(updated)

        await addBotMessage('Qual o nome da sua clínica ou consultório?')
        setStep('clinica')
        break
      }

      case 'clinica':
        addUserMessage(value)
        setAnswers(a => ({ ...a, clinica: value }))
        trackStepComplete('clinica')
        await addBotMessage('Qual a sua especialidade principal?')
        setStep('especialidade')
        break
    }
  }

  async function handleOptionSelect(option: string) {
    addUserMessage(option)

    switch (step) {
      case 'especialidade':
        setAnswers(a => ({ ...a, especialidade: option }))
        trackStepComplete('especialidade')
        await addBotMessage('Quantos pacientes você acompanha hoje?')
        setStep('pacientes')
        break

      case 'pacientes':
        setAnswers(a => ({ ...a, pacientes: option }))
        trackStepComplete('pacientes')
        await addBotMessage('Quais faixas etárias vocês atendem? Selecione todas que se aplicam.')
        setStep('faixas')
        break

      case 'desafio': {
        const finalAnswers = { ...answers, desafio: option }
        setAnswers(finalAnswers)
        trackStepComplete('desafio')

        // 2o envio: lead completo
        sendCompleteLead(finalAnswers)

        await addBotMessage(
          <>
            Perfeito, <strong>{finalAnswers.nome}</strong>! Recebi todas as informações.
          </>
        )
        await addBotMessage(
          <>
            Agora é só escolher o melhor horário para a demonstração da <strong>{finalAnswers.clinica}</strong> — são 30 minutos, online. 👇
          </>
        )
        setStep('done')
        break
      }
    }
  }

  async function handleFaixasConfirm() {
    if (selectedFaixas.length === 0) {
      track('form_validation_error', {
        ...FORM_ANALYTICS,
        step_name: 'faixas',
        reason: 'no_selection',
        source: source ?? 'unknown',
      })
      await addBotMessage('Selecione pelo menos uma faixa etária.')
      return
    }
    addUserMessage(selectedFaixas.join(', '))
    setAnswers(a => ({ ...a, faixas: selectedFaixas }))
    trackStepComplete('faixas')
    await addBotMessage('Última pergunta! Qual o maior desafio no acompanhamento hoje?')
    setStep('desafio')
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleTextSubmit()
    }
  }

  const isTextStep = step === 'nome' || step === 'telefone' || step === 'email' || step === 'clinica'

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Chat window */}
          <motion.div
            className="fixed z-[201] bottom-4 right-4 sm:bottom-6 sm:right-6 w-[calc(100%-2rem)] sm:w-[420px] max-h-[min(640px,calc(100vh-3rem))] bg-surface-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-white/10"
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 shrink-0">
              <div className="w-9 h-9 rounded-lg overflow-hidden flex items-center justify-center shrink-0">
                <Logo size={36} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-white">SecBrain</div>
                <div className="text-xs text-white/50">Agendar Demonstração</div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors duration-200 cursor-pointer bg-transparent border-none"
                aria-label="Fechar chat"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M4 4l8 8M12 4l-8 8" />
                </svg>
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3 min-h-0">
              {messages.map(msg => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className={`max-w-[85%] px-4 py-2.5 text-sm leading-[1.6] rounded-2xl ${
                    msg.from === 'bot'
                      ? 'self-start bg-white/[0.08] text-white/90 rounded-bl-md'
                      : 'self-end bg-brand-500 text-surface-950 font-medium rounded-br-md'
                  }`}
                >
                  {msg.content}
                </motion.div>
              ))}

              {/* Typing indicator */}
              {typing && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="self-start bg-white/[0.08] rounded-2xl rounded-bl-md px-4 py-3 flex gap-1"
                >
                  {[0, 1, 2].map(i => (
                    <span
                      key={i}
                      className="w-2 h-2 bg-white/40 rounded-full animate-bounce"
                      style={{ animationDelay: `${i * 0.15}s`, animationDuration: '0.8s' }}
                    />
                  ))}
                </motion.div>
              )}

              {/* Option buttons */}
              {!typing && step === 'especialidade' && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap gap-2 mt-1">
                  {ESPECIALIDADES.map(opt => (
                    <button key={opt} onClick={() => handleOptionSelect(opt)} className="chat-option">{opt}</button>
                  ))}
                </motion.div>
              )}

              {!typing && step === 'pacientes' && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap gap-2 mt-1">
                  {PACIENTES_OPTIONS.map(opt => (
                    <button key={opt} onClick={() => handleOptionSelect(opt)} className="chat-option">{opt}</button>
                  ))}
                </motion.div>
              )}

              {!typing && step === 'faixas' && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col gap-2 mt-1">
                  <div className="flex flex-wrap gap-2">
                    {FAIXAS.map(faixa => (
                      <button
                        key={faixa}
                        onClick={() =>
                          setSelectedFaixas(prev =>
                            prev.includes(faixa) ? prev.filter(s => s !== faixa) : [...prev, faixa]
                          )
                        }
                        className={`chat-option ${selectedFaixas.includes(faixa) ? 'chat-option--active' : ''}`}
                      >
                        {selectedFaixas.includes(faixa) && '✓ '}{faixa}
                      </button>
                    ))}
                  </div>
                  {selectedFaixas.length > 0 && (
                    <button onClick={handleFaixasConfirm} className="chat-option chat-option--active font-semibold mt-1">
                      Confirmar seleção →
                    </button>
                  )}
                </motion.div>
              )}

              {!typing && step === 'desafio' && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap gap-2 mt-1">
                  {DESAFIOS.map(opt => (
                    <button key={opt} onClick={() => handleOptionSelect(opt)} className="chat-option">{opt}</button>
                  ))}
                </motion.div>
              )}
            </div>

            {/* Input area */}
            {isTextStep && !typing && (
              <div className="px-4 py-3 border-t border-white/10 shrink-0">
                <p className="m-0 mb-2 text-[11px] leading-[1.45] text-white/42">
                  Ao continuar, você concorda com o uso dos seus dados para contato e agendamento,
                  conforme nossa{' '}
                  <a href="/privacidade" target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:underline">
                    política de privacidade
                  </a>.
                </p>
                <div className="flex gap-2">
                  <input
                    ref={inputRef}
                    type={step === 'email' ? 'email' : step === 'telefone' ? 'tel' : 'text'}
                    value={step === 'telefone' ? formatPhone(input) : input}
                    onChange={e =>
                      setInput(step === 'telefone' ? e.target.value.replace(/[^\d()-\s]/g, '') : e.target.value)
                    }
                    onKeyDown={handleKeyDown}
                    placeholder={
                      step === 'nome' ? 'Digite seu nome...'
                        : step === 'telefone' ? '(00) 00000-0000'
                        : step === 'email' ? 'seu@email.com'
                        : 'Nome da clínica...'
                    }
                    className="flex-1 bg-white/[0.08] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-brand-400/60 transition-colors duration-200"
                    autoComplete={step === 'email' ? 'email' : step === 'telefone' ? 'tel' : step === 'nome' ? 'name' : 'organization'}
                  />
                  <button
                    onClick={handleTextSubmit}
                    className="w-11 h-11 flex items-center justify-center bg-brand-500 rounded-xl text-surface-950 shrink-0 cursor-pointer border-none hover:bg-brand-400 transition-colors duration-200"
                    aria-label="Enviar"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Done state */}
            {step === 'done' && (
              <div className="px-4 py-3 border-t border-white/10 shrink-0 space-y-2">
                <a
                  href={calBookingUrl(answers.nome, answers.email)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-cta="cal_booking"
                  onClick={() => track('select_content', { content_type: 'cta', content_id: 'cal_booking', cta_location: 'chat_done', source: source ?? 'unknown' })}
                  className="w-full py-3 bg-brand-500 text-surface-950 font-bold text-sm rounded-xl cursor-pointer border-none hover:bg-brand-400 transition-colors duration-200 no-underline flex items-center justify-center gap-2"
                >
                  <Calendar className="w-4 h-4" />
                  Escolher horário
                </a>
                {WHATSAPP_NUMBER && (
                  <a
                    href={whatsappUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-contact-channel="whatsapp"
                    data-contact-location="chat_done"
                    className="w-full py-3 bg-[#25D366] text-white font-bold text-sm rounded-xl cursor-pointer border-none hover:bg-[#20bd5a] transition-colors duration-200 no-underline flex items-center justify-center gap-2"
                  >
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    Chamar no WhatsApp agora
                  </a>
                )}
                <button
                  onClick={onClose}
                  className="w-full py-3 bg-white/[0.07] text-white/70 font-bold text-sm rounded-xl cursor-pointer border border-white/10 hover:bg-white/[0.11] hover:text-white transition-colors duration-200"
                >
                  Fechar
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
